import { useEffect, useState } from 'react';

/** Observe the pane that actually scrolls, including a fullscreen dashboard. */
export function visibilityRoot(element) {
  return element?.parentElement?.closest('[data-chat-scroll="true"], [data-dashboard-scroll="true"], .MuiDialog-paper') || null;
}

/** Remember first visibility and pause offscreen work. */
export default function useViewportVisibility(ref, { messageId, widgetIndex } = {}) {
  const supported = typeof IntersectionObserver === 'function';
  const [state, setState] = useState(() => ({ visible: !supported, seen: !supported }));
  useEffect(() => {
    const target = ref.current;
    if (!target || typeof IntersectionObserver !== 'function') return undefined;
    let closed = false;
    const observer = new IntersectionObserver((entries) => {
      if (closed) return;
      // One delivery can contain several crossings for this card. Its last
      // observation reflects the current position, including after a reset.
      let entry;
      for (let index = entries.length - 1; index >= 0; index -= 1) {
        if (entries[index].target === target) {
          entry = entries[index];
          break;
        }
      }
      if (!entry) return;
      const visible = entry.isIntersecting && entry.intersectionRatio !== 0;
      setState((previous) => previous.visible === visible && (previous.seen || !visible)
        ? previous : { visible, seen: previous.seen || visible });
    // Edge adjacency is intersecting with zero area. A positive threshold
    // ensures another delivery when that edge moves into the scrolling pane.
    }, { root: visibilityRoot(target), threshold: [0, 0.001] });
    observer.observe(target);
    const reveal = (event) => {
      const { messageId: requested, source } = event.detail || {};
      if (requested !== messageId || widgetIndex === undefined || source?.widget_index !== widgetIndex) return;
      if (!['widget', 'widget_title'].includes(source.kind)) return;
      // A quoted widget must become addressable before Thread scrolls to its
      // source. The next observer delivery restores the actual visibility.
      setState({ visible: true, seen: true });
    };
    window.addEventListener('assistant:reveal-message', reveal);
    return () => {
      closed = true;
      observer.disconnect();
      window.removeEventListener('assistant:reveal-message', reveal);
    };
  }, [ref, messageId, widgetIndex]);
  return state;
}
